import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, RefreshCw, AlertTriangle, CheckCircle2, Search, Play } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { hostawayAPI, smoobuAPI } from '../../lib/api';
import { useEntityStore } from '../../store';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const LOW_CONFIDENCE_MIN_COUNT = 2;

// Ownership-aware status: a listing only ever reaches revenue reporting
// once it is BOTH owned and entity-assigned. Not-owned is an intentional
// skip, never a "needs attention" state -- surfacing it as such is what
// caused foreign listings to be mistaken for configuration debt.
function listingStatus(owned, entityUuid) {
  if (!owned) return { label: 'Not owned (skipped)', testId: 'skipped', className: 'bg-muted text-muted-foreground' };
  if (!entityUuid) return { label: 'Blocked', testId: 'blocked', className: 'bg-yellow-100 text-yellow-800' };
  return { label: 'Active', testId: 'active', className: 'bg-green-100 text-green-800' };
}

/**
 * Configure Hostaway: owned-listing picker, near-miss-driven mapping
 * suggestions, and the foreign-listing cleanup Preview/Apply.
 *
 * Corrects hostaway_listing_map.smoobu_property_id -- the mapping the
 * property+dates+guest dedup fallback (and the near-miss detector) key
 * off. A wrong or missing entry here is why a physically-identical flat
 * can carry two internal property records that never resolve against
 * each other. Read the current mappings, let Fan repoint the Smoobu side
 * per Hostaway listing, save through the existing listing-map endpoints.
 */
export function HostawayListingMapModal({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [togglingOwned, setTogglingOwned] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [listings, setListings] = useState([]);
  const [smoobuProperties, setSmoobuProperties] = useState([]);
  const [edits, setEdits] = useState({});

  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [acceptingKey, setAcceptingKey] = useState(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);
  const [assigningEntity, setAssigningEntity] = useState(null);
  const allEntities = useEntityStore((s) => s.entities);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mapRes, listingsRes, smoobuRes] = await Promise.all([
        hostawayAPI.getListingMap(),
        hostawayAPI.getListings(),
        smoobuAPI.getProperties(),
      ]);
      setMappings(mapRes.data?.mappings ?? []);
      setListings(listingsRes.data?.listings ?? []);
      setSmoobuProperties(smoobuRes.data?.properties ?? []);
      setEdits({});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const { data } = await hostawayAPI.getListingMapSuggestions();
      setSuggestions(data?.suggestions ?? []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      loadSuggestions();
      setCleanupPreview(null);
    }
  }, [open, load, loadSuggestions]);

  const listingName = (hostawayListingId) =>
    listings.find((l) => l.hostaway_id === hostawayListingId)?.name
      || hostawayListingId;

  // Union every synced Hostaway listing with its (possibly absent)
  // hostaway_listing_map row, so a listing with NO map entry yet --
  // exactly the no_listing_map_entry near-miss reject reason -- still
  // gets a row here instead of being invisible and unfixable. property_id
  // defaults to the listing's own hostaway_id, matching how every
  // existing mapping in this collection is keyed (hostaway_properties is
  // itself keyed by hostaway_id).
  const displayRows = useMemo(() => {
    const byId = new Map();
    for (const l of listings) {
      byId.set(l.hostaway_id, {
        hostaway_listing_id: l.hostaway_id,
        property_id: l.hostaway_id,
        smoobu_property_id: null,
        cutover_date: null,
        owned: !!l.owned,
        entity_uuid: l.entity_uuid ?? null,
        entity_name: l.entity_name ?? null,
        management_type: l.management_type ?? 'owned',
        management_fee_rate: l.management_fee_rate ?? null,
      });
    }
    for (const m of mappings) {
      byId.set(m.hostaway_listing_id, { ...byId.get(m.hostaway_listing_id), ...m });
    }
    return Array.from(byId.values());
  }, [listings, mappings]);

  const listingIdsWithEvidence = useMemo(() => {
    const ids = new Set(mappings.map((m) => m.hostaway_listing_id));
    for (const s of suggestions) ids.add(s.hostaway_listing_id);
    return ids;
  }, [mappings, suggestions]);

  const smoobuPropertyName = (smoobuPropertyId) =>
    smoobuProperties.find((p) => p.smoobu_id === smoobuPropertyId)?.name
      || null;

  const saveRow = useCallback(async (mapping) => {
    const hostawayListingId = mapping.hostaway_listing_id;
    const nextSmoobuPropertyId = edits[hostawayListingId] ?? mapping.smoobu_property_id ?? '';
    setSaving(hostawayListingId);
    try {
      await hostawayAPI.saveListingMap({
        hostaway_listing_id: hostawayListingId,
        property_id: mapping.property_id,
        smoobu_property_id: nextSmoobuPropertyId || null,
        cutover_date: mapping.cutover_date ?? null,
      });
      toast.success(`Saved mapping for ${listingName(hostawayListingId)}.`);
      await Promise.all([load(), loadSuggestions()]);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(null);
    }
  }, [edits, listings, load, loadSuggestions, onSaved]);

  const toggleOwned = useCallback(async (hostawayListingId, nextOwned) => {
    setTogglingOwned(hostawayListingId);
    try {
      const { data } = await hostawayAPI.updatePropertyOwned(hostawayListingId, nextOwned);
      setListings((prev) =>
        prev.map((l) => l.hostaway_id === hostawayListingId ? { ...l, owned: nextOwned } : l)
      );
      if (nextOwned && data?.reservations_restored) {
        toast.success(
          `${listingName(hostawayListingId)} marked owned. `
          + `${data.reservations_restored} previously-excluded reservation(s) restored.`
        );
      } else {
        toast.success(`${listingName(hostawayListingId)} marked ${nextOwned ? 'owned' : 'not owned'}.`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTogglingOwned(null);
    }
  }, [listings]);

  const assignEntity = useCallback(async (hostawayListingId, entityId) => {
    if (!entityId) return;
    const listing = listings.find((l) => l.hostaway_id === hostawayListingId);
    setAssigningEntity(hostawayListingId);
    try {
      await hostawayAPI.updatePropertyEntity(hostawayListingId, {
        entity_id: entityId,
        management_type: listing?.management_type || 'owned',
        management_fee_rate: listing?.management_fee_rate ?? null,
      });
      const entity = allEntities.find((e) => e.id === entityId);
      setListings((prev) =>
        prev.map((l) => l.hostaway_id === hostawayListingId
          ? { ...l, entity_uuid: entityId, entity_name: entity?.name ?? l.entity_name }
          : l)
      );
      toast.success(`${listingName(hostawayListingId)} assigned to ${entity?.name ?? entityId}.`);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAssigningEntity(null);
    }
  }, [listings, allEntities, onSaved]);

  // Management type/fee only mean anything once a listing has an entity
  // (the backend endpoint requires entity_id on every call), so this stays
  // disabled until assignEntity has run once -- same gating the old
  // Integrations panel used before this modal absorbed its functionality.
  const updateManagement = useCallback(async (hostawayListingId, updates) => {
    const listing = listings.find((l) => l.hostaway_id === hostawayListingId);
    if (!listing?.entity_uuid) return;
    setAssigningEntity(hostawayListingId);
    try {
      const payload = {
        entity_id: listing.entity_uuid,
        management_type: listing.management_type || 'owned',
        management_fee_rate: listing.management_fee_rate ?? null,
        ...updates,
      };
      await hostawayAPI.updatePropertyEntity(hostawayListingId, payload);
      setListings((prev) =>
        prev.map((l) => l.hostaway_id === hostawayListingId ? { ...l, ...updates } : l)
      );
      toast.success(`${listingName(hostawayListingId)} management config updated.`);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAssigningEntity(null);
    }
  }, [listings, onSaved]);

  const acceptSuggestion = useCallback(async (suggestion) => {
    const key = `${suggestion.hostaway_listing_id}|${suggestion.smoobu_property_id}`;
    setAcceptingKey(key);
    try {
      await hostawayAPI.saveListingMap({
        hostaway_listing_id: suggestion.hostaway_listing_id,
        property_id: suggestion.hostaway_listing_id,
        smoobu_property_id: suggestion.smoobu_property_id,
        cutover_date: null,
      });
      toast.success(
        `Mapped ${suggestion.hostaway_property_name || suggestion.hostaway_listing_id} `
        + `to ${suggestion.smoobu_property_name || suggestion.smoobu_property_id}.`
      );
      await Promise.all([load(), loadSuggestions()]);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAcceptingKey(null);
    }
  }, [load, loadSuggestions, onSaved]);

  const acceptableSuggestions = useMemo(
    () => suggestions.filter((s) => !s.conflict && s.supporting_pair_count >= LOW_CONFIDENCE_MIN_COUNT),
    [suggestions]
  );

  const acceptAllHighConfidence = useCallback(async () => {
    setAcceptingAll(true);
    try {
      for (const s of acceptableSuggestions) {
        await hostawayAPI.saveListingMap({
          hostaway_listing_id: s.hostaway_listing_id,
          property_id: s.hostaway_listing_id,
          smoobu_property_id: s.smoobu_property_id,
          cutover_date: null,
        });
      }
      toast.success(`Accepted ${acceptableSuggestions.length} suggestion(s).`);
      await Promise.all([load(), loadSuggestions()]);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAcceptingAll(false);
    }
  }, [acceptableSuggestions, load, loadSuggestions, onSaved]);

  const runCleanupPreview = useCallback(async () => {
    setCleanupLoading(true);
    try {
      const { data } = await hostawayAPI.cleanupPreview();
      setCleanupPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleanupLoading(false);
    }
  }, []);

  const runCleanupApply = useCallback(async () => {
    setCleanupConfirmOpen(false);
    setCleanupApplying(true);
    try {
      const { data } = await hostawayAPI.cleanupApply();
      toast.success(`Excluded ${data?.excluded_count ?? 0} foreign reservation(s).`);
      await runCleanupPreview();
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleanupApplying(false);
    }
  }, [runCleanupPreview, onSaved]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Configure Hostaway</DialogTitle>
          <DialogDescription>
            Choose which listings are Fan&apos;s (ingestion is opt-in per listing), assign each
            owned listing to an entity, repoint the Smoobu side for dedup&apos;s
            property+dates+guest fallback, and clean up reservations already ingested from a
            listing that turns out to be foreign. Not-owned listings are intentionally skipped,
            never counted as needing action.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[40vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-3 py-2 font-medium">Hostaway listing</th>
                  <th className="text-left px-3 py-2 font-medium">Owned</th>
                  <th className="text-left px-3 py-2 font-medium">Entity</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Fee</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Mapped Smoobu property</th>
                  <th className="text-left px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((m) => {
                  const current = edits[m.hostaway_listing_id] ?? m.smoobu_property_id ?? '';
                  const dirty = current !== (m.smoobu_property_id ?? '');
                  const needsReview = !m.owned && !listingIdsWithEvidence.has(m.hostaway_listing_id);
                  const status = listingStatus(m.owned, m.entity_uuid);
                  return (
                    <tr key={m.hostaway_listing_id} className={`border-b ${needsReview ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="text-xs font-mono">{m.hostaway_listing_id}</div>
                        <div className="text-xs text-muted-foreground">
                          {listingName(m.hostaway_listing_id)}
                        </div>
                        {needsReview && (
                          <Badge variant="outline" className="mt-1 text-[10px] text-amber-700 dark:text-amber-400 border-amber-300">
                            Needs review
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Switch
                          data-testid={`listing-owned-switch-${m.hostaway_listing_id}`}
                          checked={!!m.owned}
                          disabled={togglingOwned === m.hostaway_listing_id}
                          onCheckedChange={(checked) => toggleOwned(m.hostaway_listing_id, checked)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {m.owned ? (
                          <Select
                            value={m.entity_uuid ?? ''}
                            onValueChange={(val) => assignEntity(m.hostaway_listing_id, val)}
                            disabled={assigningEntity === m.hostaway_listing_id}
                          >
                            <SelectTrigger
                              className="w-full h-9 text-xs"
                              data-testid={`hostaway-entity-select-${m.hostaway_listing_id}`}
                            >
                              <SelectValue placeholder="Assign entity...">
                                {assigningEntity === m.hostaway_listing_id
                                  ? 'Saving...'
                                  : (m.entity_name || 'Assign entity...')}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {allEntities.map((e) => (
                                <SelectItem key={e.id} value={e.id} className="text-xs">
                                  {e.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span
                            className="text-xs text-muted-foreground"
                            data-testid={`hostaway-not-owned-${m.hostaway_listing_id}`}
                          >
                            Not owned (skipped)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {m.owned ? (
                          <Select
                            value={m.management_type || 'owned'}
                            onValueChange={(val) => updateManagement(m.hostaway_listing_id, {
                              management_type: val,
                              management_fee_rate: val === 'managed' ? (m.management_fee_rate ?? 0.20) : null,
                            })}
                            disabled={!m.entity_uuid || assigningEntity === m.hostaway_listing_id}
                          >
                            <SelectTrigger className="w-full h-9 text-xs" data-testid={`hostaway-management-type-${m.hostaway_listing_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owned" className="text-xs">Owned</SelectItem>
                              <SelectItem value="managed" className="text-xs">Managed</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {m.owned && m.management_type === 'managed' ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              data-testid={`hostaway-management-fee-${m.hostaway_listing_id}`}
                              className="w-14 h-8 text-xs border rounded px-1 text-center"
                              value={m.management_fee_rate != null ? Math.round(m.management_fee_rate * 100) : 20}
                              min={1}
                              max={100}
                              disabled={!m.entity_uuid || assigningEntity === m.hostaway_listing_id}
                              onChange={(e) => {
                                const rate = parseInt(e.target.value, 10);
                                if (!isNaN(rate)) {
                                  setListings((prev) =>
                                    prev.map((row) => row.hostaway_id === m.hostaway_listing_id
                                      ? { ...row, management_fee_rate: rate / 100 }
                                      : row)
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                const rate = parseInt(e.target.value, 10);
                                if (!isNaN(rate) && rate > 0 && rate <= 100) {
                                  updateManagement(m.hostaway_listing_id, { management_fee_rate: rate / 100 });
                                }
                              }}
                            />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${status.className}`}
                          data-testid={`hostaway-status-${status.testId}-${m.hostaway_listing_id}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          data-testid={`listing-map-select-${m.hostaway_listing_id}`}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-xs"
                          value={current}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [m.hostaway_listing_id]: e.target.value }))
                          }
                        >
                          <option value="">(unmapped)</option>
                          {current && !smoobuPropertyName(current) && (
                            <option value={current}>{current} (not in synced list)</option>
                          )}
                          {smoobuProperties.map((p) => (
                            <option key={p.smoobu_id} value={p.smoobu_id}>
                              {p.name || p.smoobu_id}
                            </option>
                          ))}
                        </select>
                        {current && !smoobuPropertyName(current) && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                            {current} not found in synced Smoobu properties.
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!dirty || saving === m.hostaway_listing_id}
                          onClick={() => saveRow(m)}
                          data-testid={`listing-map-save-${m.hostaway_listing_id}`}
                        >
                          {saving === m.hostaway_listing_id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Save className="h-3.5 w-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {displayRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground text-xs">
                      No synced Hostaway listings yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end -mt-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>

        {/* Suggested mappings, derived from near-miss pair evidence */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">
              Suggested mappings {suggestions.length > 0 ? `(${suggestions.length})` : ''}
            </h4>
            {acceptableSuggestions.length > 1 && (
              <Button
                size="sm"
                variant="outline"
                onClick={acceptAllHighConfidence}
                disabled={acceptingAll || acceptingKey !== null}
                data-testid="suggestions-accept-all-btn"
              >
                {acceptingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                Accept all &ge;{LOW_CONFIDENCE_MIN_COUNT} pairs ({acceptableSuggestions.length})
              </Button>
            )}
          </div>
          {suggestionsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : suggestions.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No near-miss evidence yet. Run Migration Audit Preview to generate candidates.
            </p>
          ) : (
            <div className="overflow-x-auto max-h-[30vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 font-medium">Hostaway listing</th>
                    <th className="text-left px-3 py-2 font-medium">Suggested Smoobu property</th>
                    <th className="text-left px-3 py-2 font-medium">Support</th>
                    <th className="text-left px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => {
                    const key = `${s.hostaway_listing_id}|${s.smoobu_property_id}`;
                    return (
                      <tr key={key} className="border-b">
                        <td className="px-3 py-2 text-xs">
                          <div>{s.hostaway_property_name}</div>
                          <div className="text-muted-foreground font-mono">{s.hostaway_listing_id}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div>{s.smoobu_property_name}</div>
                          <div className="text-muted-foreground font-mono">{s.smoobu_property_id}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex flex-wrap gap-1 items-center">
                            <span data-testid={`suggestion-count-${key}`} className="tabular-nums">
                              {s.supporting_pair_count} pair{s.supporting_pair_count !== 1 ? 's' : ''}
                            </span>
                            {s.conflict && (
                              <Badge variant="destructive" className="text-[10px]" data-testid={`suggestion-conflict-${key}`}>
                                Conflict
                              </Badge>
                            )}
                            {s.low_confidence && !s.conflict && (
                              <Badge variant="outline" className="text-[10px] text-amber-700 dark:text-amber-400 border-amber-300">
                                Low confidence
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant={s.conflict ? 'outline' : 'default'}
                            disabled={acceptingKey === key || acceptingAll}
                            onClick={() => acceptSuggestion(s)}
                            data-testid={`suggestion-accept-${key}`}
                          >
                            {acceptingKey === key
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : 'Accept'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {suggestions.some((s) => s.conflict) && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Conflicting rows point the same Hostaway listing at two different Smoobu
                  properties -- accept the correct one manually, never bulk-accepted.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Foreign-listing cleanup: Preview / Apply */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Foreign-listing cleanup</h4>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={runCleanupPreview}
                disabled={cleanupLoading || cleanupApplying}
                data-testid="cleanup-preview-btn"
              >
                {cleanupLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Search className="h-3.5 w-3.5 mr-1.5" />}
                Preview
              </Button>
              <Button
                size="sm"
                onClick={() => setCleanupConfirmOpen(true)}
                disabled={!cleanupPreview || cleanupPreview.total_reservation_count === 0 || cleanupLoading || cleanupApplying}
                data-testid="cleanup-apply-btn"
              >
                {cleanupApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
                Apply
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Reservations already ingested from a listing now marked not-owned. Preview is
            read-only; Apply reversibly excludes them (never deletes) from every revenue,
            forecast, and statement surface. Toggling a listing back to owned above restores
            its reservations automatically.
          </p>
          {cleanupPreview && (
            cleanupPreview.total_reservation_count === 0 ? (
              <div className="rounded-lg border p-3 text-center" data-testid="cleanup-preview-empty">
                <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">
                  No reservations from not-owned listings. Nothing to clean up.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium">Listing</th>
                      <th className="text-right px-3 py-2 font-medium">Reservations</th>
                      <th className="text-right px-3 py-2 font-medium">Sum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleanupPreview.listings.map((l) => (
                      <tr key={l.hostaway_listing_id} className="border-b">
                        <td className="px-3 py-2 text-xs">
                          <div>{l.listing_name}</div>
                          <div className="text-muted-foreground font-mono">{l.hostaway_listing_id}</div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums" data-testid={`cleanup-count-${l.hostaway_listing_id}`}>
                          {l.reservation_count}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {l.sum_total_price.toFixed(2)}
                          {l.quarantined_count > 0 && (
                            <div className="text-[10px] text-amber-600 dark:text-amber-400 font-normal">
                              +{l.quarantined_count} quarantined (amount unknown, excluded from sum)
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="px-3 py-2 text-xs font-medium">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium" data-testid="cleanup-total-count">
                        {cleanupPreview.total_reservation_count}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {cleanupPreview.total_sum.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          )}
        </div>

        <Dialog open={cleanupConfirmOpen} onOpenChange={setCleanupConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm foreign-listing cleanup</DialogTitle>
              <DialogDescription>
                Excludes {cleanupPreview?.total_reservation_count ?? 0} reservation(s) from
                not-owned listings across every revenue, forecast, and statement surface.
                Reversible: not deleted, and restored automatically if a listing is toggled
                back to owned.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCleanupConfirmOpen(false)}>
                Cancel
              </Button>
              <Button data-testid="cleanup-confirm-apply-btn" onClick={runCleanupApply}>
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
